import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { knowledgeApi, managementApi } from '../api/services'
import type { Document, UploadResponse, ExtractionTestRequest, ExtractionTestResponse } from '../api/types'

// 查询键工厂
export const knowledgeKeys = {
  all: ['knowledge'] as const,
  documents: (statusFilter?: string) => [...knowledgeKeys.all, 'documents', statusFilter] as const,
  document: (id: string) => [...knowledgeKeys.all, 'document', id] as const,
  chunks: (documentId: string) => [...knowledgeKeys.all, 'chunks', documentId] as const,
  status: (documentId: string) => [...knowledgeKeys.all, 'status', documentId] as const,
}

// 文档列表查询
export const useDocuments = (statusFilter?: string) => {
  return useQuery({
    queryKey: knowledgeKeys.documents(statusFilter),
    queryFn: () => managementApi.getDocuments(statusFilter),
    enabled: !!statusFilter, // 条件查询
  })
}

// 文档详情查询
export const useDocument = (documentId: string) => {
  return useQuery({
    queryKey: knowledgeKeys.document(documentId),
    queryFn: () => managementApi.getDocument(documentId),
    enabled: !!documentId,
  })
}

// 文档chunks查询
export const useDocumentChunks = (documentId: string) => {
  return useQuery({
    queryKey: knowledgeKeys.chunks(documentId),
    queryFn: () => managementApi.getDocumentChunks(documentId),
    enabled: !!documentId,
  })
}

// 文档状态查询
export const useDocumentStatus = (documentId: string) => {
  return useQuery({
    queryKey: knowledgeKeys.status(documentId),
    queryFn: () => managementApi.getDocumentStatus(documentId),
    enabled: !!documentId,
    // 注意：轮询机制已移除，改用WebSocket实时推送
    // refetchInterval: false
  })
}

// 上传文档 mutation
export const useUploadDocument = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (progress: number) => void }) =>
      knowledgeApi.uploadDocument(file, onProgress),
    onSuccess: () => {
      // 使文档列表查询失效
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.documents() })
      // 使知识库状态查询失效，确保嵌入模型设置能及时更新
      queryClient.invalidateQueries({ queryKey: ['company', 'knowledge-base-documents'] })
    },
  })
}

// 删除文档 mutation
export const useDeleteDocument = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => managementApi.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.documents() })
      // 使知识库状态查询失效，确保嵌入模型设置能及时更新
      queryClient.invalidateQueries({ queryKey: ['company', 'knowledge-base-documents'] })
    },
  })
}

// 知识提取测试 mutation
export const useExtractionTest = () => {
  return useMutation({
    mutationFn: (request: ExtractionTestRequest) => knowledgeApi.extractionTest(request),
  })
}